import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { DateModel } from '@app/core/components/date-range/date-range.component';
import { Item } from '@app/core/components/listbox/listbox.component';
import { RememberService } from '@app/core/services/remember.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { ProductService, ResourceItem } from '@app/services/product.service';
import { SrOperationsGet, SrOperationService } from '@app/services/sr-operation.service';

@Component({
    selector: 'tt-sr-operations',
    templateUrl: './sr-operations.component.html',
    styleUrls: ['./sr-operations.component.css'],
    standalone: false,
})
export class SrOperationsComponent implements OnInit, OnDestroy {
    public page?: SrOperationsGet;
    public operations: Item[] = [];
    public searching = false;
    private loadVersion = 0;
    private destroyed = false;
    private refreshTimer?: ReturnType<typeof setTimeout>;
    private readonly intervals = ['day', 'week', 'month', 'quarter', 'biannual', 'year', 'yeartodate', 'minmax'];

    constructor(
        private readonly operation: SrOperationService,
        private readonly product: ProductService,
        private readonly remember: RememberService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) { this.navigator = useNavigatorFor(navigator, this); }

    public async ngOnInit(): Promise<void> {
        const page = await this.operation.getOperations();
        if (this.destroyed) return;
        this.page = page;
        this.updateRefreshTimer();
        await this.loadOperations();
    }

    public searchResources = (params?: { value?: string }): Promise<ResourceItem[]> => {
        return this.product.searchResources({ resource_searchtext: params?.value ?? this.page?.resource_searchtext ?? '' });
    };

    public async onResourceTextChanged(value: string): Promise<void> {
        if (!this.page) return;
        if (value === '') await this.clearResource();
        else this.page.resource_searchtext = value;
    }

    public async onResourceSelected(resource: ResourceItem): Promise<void> {
        if (!this.page) return;
        this.page.resource_id = resource.item_id;
        this.page.resource_searchtext = resource.item_name;
        // The search control models the name, while the legacy remembered value is the ID.
        await Promise.all([this.remember.remember('w_sroperations.resource_id', resource.item_id), this.loadOperations()]);
    }

    public async clearResource(): Promise<void> {
        if (!this.page || (this.page.resource_id === '' && this.page.resource_searchtext === '')) return;
        this.page.resource_id = '';
        this.page.resource_searchtext = '';
        await Promise.all([this.remember.remember('w_sroperations.resource_id', ''), this.loadOperations()]);
    }

    public async onViewChanged(value: string): Promise<void> {
        if (!this.page || this.page.bg_sroperations_view === value) return;
        this.page.bg_sroperations_view = value;
        this.updateRefreshTimer();
        await this.loadOperations();
    }

    public async onRefreshChanged(value: '0' | '1'): Promise<void> {
        if (!this.page || this.page.refresh_active === value) return;
        this.page.refresh_active = value;
        this.updateRefreshTimer();
        if (value === '1') await this.loadOperations();
    }

    public async onDatesChanged(dates: DateModel): Promise<void> {
        if (!this.page) return;
        const index = this.intervals.indexOf(dates.dateselector_index);
        const dateRange = dates.date_fom + dates.date_tom;
        const changed = this.page.date_fom + this.page.date_tom !== dateRange;
        this.page = { ...this.page, ...dates, dateselector_index: index === -1 ? dates.dateselector_index : String(index + 1), date_fomtom: dateRange };
        if (changed) await this.loadOperations();
    }

    public async loadOperations(): Promise<void> {
        const version = ++this.loadVersion;
        const page = this.page;
        if (this.destroyed || !page?.bg_sroperations_view?.trim() || !page.refresh_active?.trim() ||
            [page.date_fom, page.date_tom, page.dateselector_index, page.resource_id, page.resource_searchtext, page.searchtext].some(value => value === undefined || value === null)) {
            this.searching = false;
            return;
        }
        this.searching = true;
        this.operations = [];
        try {
            const operations = await this.operation.getOperationList({ ...page });
            if (!this.destroyed && version === this.loadVersion) this.operations = operations;
        } finally { if (version === this.loadVersion) this.searching = false; }
    }

    private updateRefreshTimer(): void {
        if (this.refreshTimer !== undefined) clearTimeout(this.refreshTimer);
        this.refreshTimer = undefined;
        if (this.destroyed || this.page?.bg_sroperations_view !== 'ACTIVE' || this.page.refresh_active !== '1') return;
        this.refreshTimer = setTimeout(() => {
            this.updateRefreshTimer();
            void this.loadOperations().catch((error: unknown) => console.error('Unable to refresh SR operations.', error));
        }, 60000);
    }

    public ngOnDestroy(): void {
        this.destroyed = true;
        this.loadVersion++;
        this.searching = false;
        if (this.refreshTimer !== undefined) clearTimeout(this.refreshTimer);
    }
}
